import { type Dispatch, type FC, type ReactNode, Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isEmpty, _notNil } from '@/littledash';
import type { AnimalSpecies, AnimalStrain } from '@/model/Animal.model';
import { type AnimalSexMap } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { featuresSelector } from '@/support/Selectors';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { sortAscByProperty } from '@/utils/array.utils';
import type { EntityFieldRequiredMap } from '@/utils/entitySchema/useEntitySchema';
import Loading from '@/components/Loading';

import { getMetaData } from './../Colony.utils';
import { initAnimalSettingsV16 } from './../Colony.utils.v16';
import AnimalsTableV16 from './AnimalsTableV16';

const featureFlagSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

type AnimalSexDisplay = (typeof AnimalSexMap)[keyof typeof AnimalSexMap];

interface CreationAnimalRow extends Record<string, unknown> {
  id?: number;
  cage_name?: string;
  cage_catalog?: string;
  animal_id?: string;
  name?: string;
  sex?: AnimalSexDisplay;
  dob?: string | null;
  species?: AnimalSpecies['name'];
  strain?: AnimalStrain['name'];
  metadataAdded?: boolean;
  meta?: Record<string, unknown>;
}

interface CageRow {
  name: string;
  catalog: string;
  population: number;
}

interface AnimalsState {
  animals: {
    species: Array<AnimalSpecies>;
    settings: CustomGridSettings;
    v16Settings?: SpreadsheetV16Settings;
    metadata: Array<MetadataField>;
    cellErrors: Record<string, string>;
    data: Array<CreationAnimalRow>;
    overlay: ReactNode;
  };
  cages: {
    data: Array<CageRow>;
  };
}

interface AnimalsAction {
  type: string;
  id?: string;
  data?: Record<string, unknown> | string;
}

interface AnimalsProps {
  state: AnimalsState;
  dispatch: Dispatch<AnimalsAction>;
  entityFieldRequiredMap: EntityFieldRequiredMap;
}

const Animals: FC<AnimalsProps> = ({ state, dispatch, entityFieldRequiredMap }) => {
  const {
    animals: { species, settings, v16Settings, metadata, cellErrors, data: animalData, overlay },
    cages: { data: cageData },
  } = state;
  const { unknownSexEnabled } = useSelector(featureFlagSelector);
  const ref = useRef<HotTableRef>(null);
  const [isInvalid, setIsInvalid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  const { response } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  useEffect(() => {
    if (!_notNil(response)) {
      return;
    }

    if (response.type !== 'success') {
      updateApiError(response);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const update: Record<string, unknown> = {};
    update.v16Settings = initAnimalSettingsV16(
      response.body.data as unknown as Array<AnimalSpecies>,
      true,
      false,
      undefined,
      undefined,
      v16Settings,
      entityFieldRequiredMap,
      unknownSexEnabled
    ).settings;
    update.species = response.body.data;

    getMetaData(MetadataTypes.subject_meta)
      .then((metaResponse) => {
        if (cancelled) {
          return;
        }
        const data = (metaResponse?.body ?? []) as Array<MetadataField>;
        updateGlossaries(data);
        update.metadata = animalData.some((a) => a.metadataAdded === true) ? metadata : data;
        update.data = createAnimalData();
        updateAnimals(update);
        dispatch({ type: 'stepReady' });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        updateApiError(error);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, entityFieldRequiredMap]);

  useEffect(() => {
    const newAnimals = createAnimalData().sort(sortAscByProperty('cage_catalog'));
    updateAnimals({ data: newAnimals });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cageData]);

  const updateAnimals = (update: Record<string, unknown>): void => {
    dispatch({ type: 'update', id: 'animals', data: update });
  };

  const updateOverlay = (newOverlay: ReactNode): void => {
    dispatch({ type: 'update', id: 'animals', data: { overlay: newOverlay } });
  };

  const updateCellErrors = (errors: Record<string, string>): void => {
    dispatch({ type: 'update', id: 'animals', data: { cellErrors: errors } });
  };

  const updateApiError = (apiError: unknown): void => {
    dispatch({ type: 'update', id: 'animals', data: apiError as Record<string, unknown> });
  };

  const updateSettings = (newSettings: SpreadsheetV16Settings): void => {
    dispatch({ type: 'update', id: 'animals', data: { v16Settings: newSettings } });
  };

  const updateMetadata = (updatedMetadata: Array<MetadataField>): void => {
    dispatch({
      type: 'update',
      id: 'animals',
      data: { metadata: updatedMetadata, data: [...animalData.map((a) => ({ ...a, metadataAdded: true }))] },
    });
  };

  const updateGlossaries = (metaGlossaries: Array<MetadataField>): void => {
    dispatch({ type: 'update', id: 'animals', data: { metaGlossaries } });
  };

  const createNewAnimal = (
    animalCount: number,
    cageName: string,
    cageCatalog: string,
    animalID: number
  ): CreationAnimalRow => {
    const knownFields: Record<string, unknown> = {
      id: animalCount,
      cage_name: cageName,
      cage_catalog: cageCatalog,
      animal_id: `Animal ${animalID}`,
      name: `Animal ${animalID}`,
    };

    if (!_notNil(settings) || !_notNil(settings.columns)) {
      return {};
    }

    return settings.columns.reduce<CreationAnimalRow>((animal, col) => {
      const field = col.data as string;
      animal[field] = knownFields[field] ?? '';
      return animal;
    }, {});
  };

  const createAnimalData = (): Array<CreationAnimalRow> => {
    let newAnimalData: Array<CreationAnimalRow> = animalData
      .map((animal) => ({
        ...animal,
        cage_name: cageData.find((newCage) => newCage.catalog === animal.cage_catalog)?.name,
      }))
      .filter((animal) => _notNil(animal.cage_name));

    cageData.forEach(({ name: cageName, catalog: cageCatalog, population }) => {
      if (_notNil(cageName)) {
        const currentCageAnimals = newAnimalData.filter((animal) => animal.cage_catalog === cageCatalog);
        const count = currentCageAnimals.length;
        const animalDiff = Math.abs(population - count);
        if (population < count) {
          const animalsToRemove = currentCageAnimals.slice(animalDiff * -1);
          newAnimalData = newAnimalData.filter((tempAnimal) => !animalsToRemove.includes(tempAnimal));
        } else if (population > count) {
          let runningCount = newAnimalData.length + 1;
          let animalID = count + 1;
          for (let i = 0; i < animalDiff; i++) {
            newAnimalData.push(createNewAnimal(runningCount, cageName, cageCatalog, animalID));
            animalID++;
            runningCount++;
          }
        }
      }
    });

    const newOverlay: ReactNode = _isEmpty(newAnimalData) ? (
      <Fragment>
        <h3 className="mb2">No animals added</h3>
        <p style={{ maxWidth: '520px', alignSelf: 'center' }}>
          Click continue to create empty cages, or go back and increase the number of animals for you cages.
        </p>
      </Fragment>
    ) : (
      ''
    );
    updateOverlay(newOverlay);

    return newAnimalData;
  };

  useEffect(() => {
    if (isInvalid) {
      applyInvalidError();
    } else {
      const { 'auto-validation': _autoVal, ...remainingErrors } = cellErrors;
      void _autoVal;
      updateCellErrors(remainingErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalid]);

  const applyInvalidError = (): void => {
    updateCellErrors({ ...cellErrors, 'auto-validation': 'There are invalid cells' });
  };

  return loading ? (
    <div style={{ height: 650 }}>
      <Loading txt="Loading species..." />
    </div>
  ) : (
    <div className="ow-spreadsheet-styles">
      <AnimalsTableV16
        data={animalData as Array<Record<string, unknown>>}
        innerRef={ref as React.RefObject<HotTableRef>}
        metadata={metadata}
        setMetadata={updateMetadata}
        setInvalid={setIsInvalid}
        overlay={overlay}
        maxRows={animalData.length}
        species={species}
        entityFieldRequiredMap={entityFieldRequiredMap}
        existingSettings={v16Settings}
        setSettings={updateSettings}
      />
    </div>
  );
};

export default Animals;
